/**
 * Veřejný seznam zpracovatelů — čl. 28 odst. 2 a 4 GDPR (KAN-125).
 *
 * Do 2026-08-19 byl seznam pole konstant přímo v ``Subprocessors.tsx``
 * a „poslední aktualizace" ručně psané datum. Znamenalo to, že výměna
 * dodavatele je deploy — a že datum mohlo tvrdit cokoli, protože ho
 * s obsahem nic nesvazovalo. Teď obojí přichází z API a
 * ``last_updated`` je odvozené z ``max(updated_at)``.
 *
 * ``lang`` cestuje v queryKey: účel a mechanismus přenosu jsou
 * lokalizované věty, takže po přepnutí jazyka by cache jinak servírovala
 * ten předchozí.
 */

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { DEFAULT_LANG } from "@/i18n";
import { apiFetch } from "@/lib/api";

const API = "/api/v1";

export interface PublicSubprocessor {
  key: string;
  name: string;
  country: string;
  purpose: string;
  transfer_mechanism: string;
  /** ``null`` = služba bez zpracovatelské smlouvy (KAN-1938). */
  dpa_url: string | null;
  /**
   * ``false`` = služba, která pro nás osobní údaje nezpracovává (ARES,
   * přihlášení přes Google). Stránka ji ukáže v sekci „Další služby"
   * a nepočítá ji mezi subzpracovatele (KAN-1938).
   */
  is_processor: boolean;
  /** ``false`` = vyřazený zpracovatel; zůstává v seznamu jako doklad. */
  is_active: boolean;
}

export interface PublicSubprocessors {
  items: PublicSubprocessor[];
  /** ISO 8601, nebo ``null`` u prázdného seznamu. */
  last_updated: string | null;
}

export function usePublicSubprocessors() {
  const { i18n } = useTranslation();
  const lang = i18n.language || DEFAULT_LANG;
  return useQuery<PublicSubprocessors>({
    queryKey: ["public", "subprocessors", lang] as const,
    queryFn: () =>
      apiFetch<PublicSubprocessors>(
        `${API}/public/subprocessors?lang=${encodeURIComponent(lang)}`,
      ),
    // Odpovídá Cache-Control na BE (10 minut).
    staleTime: 10 * 60 * 1000,
  });
}
